
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SimpleAvatarUpload from '@/components/common/SimpleAvatarUpload';

interface AvatarUploadProps {
  avatarUrl: string | null;
  avatarPreview: string | null;
  userId: string;
  onAvatarChange: (file: File | null, preview: string | null) => void;
  isUploading?: boolean;
  avatarFile?: File | null;
  onSaveAvatar?: () => Promise<boolean>;
  profile?: any;
}

const AvatarUpload: React.FC<AvatarUploadProps> = ({ 
  avatarUrl, 
  avatarPreview,
  userId,
  onAvatarChange,
  isUploading = false,
  avatarFile,
  onSaveAvatar,
  profile
}) => {
  const { t } = useTranslation('dashboard');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('profileManagement.profilePhoto')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-6">
          <div className="flex flex-col items-center gap-4">
            <SimpleAvatarUpload
              avatarUrl={avatarUrl}
              avatarPreview={avatarPreview}
              userId={userId}
              onAvatarChange={onAvatarChange}
              isUploading={isUploading}
              fallbackText={profile?.first_name?.charAt(0) || profile?.last_name?.charAt(0) || 'U'}
            />
          </div>
          <div className="flex-1">
            <p className="text-sm text-muted-foreground mb-3">
              {t('profileManagement.clientPhotoDescription')}
            </p>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>• {t('profileManagement.uploadInstructions')}</p>
              <p>• {t('profileManagement.aspectRatioInfo')}</p>
              <p>• {t('profileManagement.maxFileSize')}</p>
              <p>• {t('profileManagement.supportedFormats')}</p>
            </div>
            {avatarFile && onSaveAvatar && (
              <div className="mt-4 p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium text-success mb-2">
                  {t('profileManagement.newPhotoSelected', { filename: avatarFile.name })}
                </p>
                <Button 
                  onClick={onSaveAvatar}
                  disabled={isUploading}
                  size="sm"
                >
                  {isUploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isUploading ? t('profileManagement.uploading') : t('profileManagement.saveNewPhoto')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AvatarUpload;
